import { apiPost } from "@/shared/api";
import type { MailOverride, MailPreviewRead } from "@/features/mail/api";
import type { EditablePackage } from "./packagesEditing";
import { deriveSupplierMailStatusFromRaw } from "./supplierMailStatus";
import type { Order } from "./types";

export type WebFurnitureMailStatusState = "confirmed_imap" | "pending_imap" | "never_sent";

/** Ordini Web Furniture nella selezione: gate del bottone "Invia ordine WF".
 *  Il backend marca ``wfEligible`` (SKU nel catalogo ``webfurniture``, non-AS,
 *  fornitore ``unknown``). WF non è un ``FornitoreKind`` Poleepo: questo flag è
 *  l'unico segnale affidabile, così il tasto agisce SOLO sugli ordini WF. */
export function ordersWithWebFurnitureSku<T extends Pick<Order, "wfEligible">>(orders: T[]): T[] {
  return orders.filter((o) => o.wfEligible === true);
}

/** Ordini della selezione da ordinare via mail a ``fornitore`` (id backend:
 *  ``aurora``, …): gate del suo tasto d'invio.
 *
 *  Legge ``fornitoreMail``, che il backend deriva dal **PIM**. Web Furniture
 *  resta su ``wfEligible`` (qui sopra) e non passa da qui: il suo catalogo OM
 *  ha 587 SKU contro i 376 del PIM, e spostare il gate gli spegnerebbe il
 *  tasto su ordini che oggi funzionano. */
export function ordersWithFornitoreMail<T extends Pick<Order, "fornitoreMail">>(
  orders: T[],
  fornitore: string,
): T[] {
  return orders.filter((o) => o.fornitoreMail === fornitore);
}

/** Rotte d'invio del fornitore. Un solo pattern per tutti: ``webfurniture`` e
 *  ``aurora`` differiscono soltanto per il segmento nel path. */
const mailOrderPath = (fornitore: string, coda: string): string =>
  `/api/suppliers/${encodeURIComponent(fornitore)}/orders/${coda}`;

/** Prefisso dei campi su ``Order.raw`` e destinatario, per fornitore: specchio
 *  di ``FORNITORI_ORDINE_MAIL`` (backend ``shared/constants.py``). Serve al
 *  badge, che legge lo stato dal raw dell'ordine senza un round-trip — l'unico
 *  posto del frontend che deve sapere come si chiamano quei campi. */
type ProfiloMail = {
  prefix: string;
  recipient: string;
  trasporto: "mail" | "http";
  nome: string;
};

const PROFILO_WF: ProfiloMail = {
  prefix: "wf",
  recipient: "sales@web-furniture.com",
  trasporto: "mail",
  nome: "Web Furniture",
};
const PROFILI_MAIL: Record<string, ProfiloMail> = {
  webfurniture: PROFILO_WF,
  aurora: {
    prefix: "aurora",
    recipient: "info@aurorabedding.eu",
    trasporto: "mail",
    nome: "Aurora Bedding",
  },
  // Mauro Ferretti non ha un indirizzo: l'ordine parte con un POST al suo
  // portale, e il "destinatario" è quell'URL. Il badge non cambia — legge il
  // prefisso come per gli altri — ma quello che mostra on-hover deve dire la
  // verità su dove è finito l'ordine.
  mauroferretti: {
    prefix: "mf",
    recipient: "https://mauroferretti.it/ordini_it",
    trasporto: "http",
    nome: "Mauro Ferretti",
  },
};

/** L'ordine a questo fornitore parte con una chiamata al suo portale, non con
 *  una mail.
 *
 *  ⛔ Serve al ramo SMTP (che è quello della produzione): lì l'invio passa dal
 *  popup di composizione, e mostrare una bozza di mail — destinatario, oggetto,
 *  corpo — per un ordine che va in POST sarebbe una UI che mente. Il flag viene
 *  dal profilo, non da un elenco di id scritto a mano: un fornitore nuovo si
 *  dichiara in un posto solo. */
export function fornitoreViaHttp(fornitore: string): boolean {
  return PROFILI_MAIL[fornitore]?.trasporto === "http";
}

/** Nome per esteso del fornitore, per i titoli. ``NOMI_FORNITORE_MAIL`` porta
 *  le sigle dei messaggi brevi ("WF"): qui serve il nome che l'operatore legge
 *  sul popup prima di confermare un ordine, e sbagliarlo è un errore operativo,
 *  non un refuso. */
export function nomeFornitoreMail(fornitore: string): string {
  return PROFILI_MAIL[fornitore]?.nome ?? fornitore;
}

/** Nome leggibile del fornitore-via-mail, per messaggi e tooltip. */
export const NOMI_FORNITORE_MAIL: Record<string, string> = {
  webfurniture: "WF",
  aurora: "Aurora",
  mauroferretti: "Mauro Ferretti",
};

export type WebFurnitureMailStatusDetail = {
  state: WebFurnitureMailStatusState;
  app_sent_at: string | null;
  app_message_id: string | null;
  app_attachment: string | null;
  app_attempts: number;
  imap_confirmed_at: string | null;
  imap_message_id: string;
  imap_attachment: string;
  recipient: string;
};

export type WebFurnitureVerifyMailResult = {
  order_id: number;
  reference: string;
  scan_metrics: Record<string, unknown>;
  status: WebFurnitureMailStatusDetail;
};

export type WebFurnitureBulkError = {
  order_id: string;
  message: string;
};

export type WebFurnitureSentItem = {
  order_id: number;
  reference: string;
  subject: string;
  attachment_name: string;
  message_id: string;
};

export type WebFurnitureSendBulkResult = {
  requested: number;
  recipient: string;
  sent: WebFurnitureSentItem[];
  errors: WebFurnitureBulkError[];
  /** Avvisi non bloccanti: es. ordine già spedito su Poleepo, colli non applicati. */
  warnings: string[];
};

/** Collo WF: alias del tipo collo di dominio condiviso (unica fonte di verità,
 *  vedi packagesEditing.ts). Fluisce fino agli override di sendWebFurnitureOrderBulk. */
export type WebFurniturePackage = EditablePackage;

export type WebFurniturePreviewOrder = {
  order_id: number;
  reference: string;
  shipping: {
    customer_name: string;
    address: string;
    postcode: string;
    city: string;
    province: string;
    country: string;
    phone: string;
  };
  items: Array<{ sku: string; title: string; quantity: number }>;
  /** Colli proposti: salvati sull'ordine o default dal catalogo WF (multi-cartone). */
  packages: WebFurniturePackage[];
  /** Baseline dal catalogo: base del ricalcolo stepper. */
  unit_packages: WebFurniturePackage[];
  packages_source: "saved" | "catalog";
  already_sent: boolean;
  warnings: string[];
};

export type WebFurniturePreviewResult = {
  orders: WebFurniturePreviewOrder[];
  errors: WebFurnitureBulkError[];
};

/** Anteprima invio WF per il popup di conferma: dati spedizione, righe e
 *  colli proposti per ordine (multi-cartone dal catalogo). Solo DB lato
 *  server, nessuna connessione Thunderbird. */
export function fetchWebFurniturePreview(
  orderIds: string[],
  fornitore = "webfurniture",
): Promise<WebFurniturePreviewResult> {
  return apiPost<WebFurniturePreviewResult>(mailOrderPath(fornitore, "mail-preview"), {
    order_ids: orderIds,
  });
}

/** Bulk "Invia ordine WF": una mail Thunderbird per ordine selezionato a
 *  Web Furniture, con la LDV PDF allegata (generata se manca).
 *  ``packagesOverrides`` (popup conferma, chiave=reference): colli editati →
 *  persistiti su Order.package_dimensions + LDV rigenerata per quegli ordini.
 *  ``opts`` assente/senza ``preview`` → body e ritorno invariati (retrocompat,
 *  vedi test). ``preview: true`` → union esplicita (bozze senza invio, va
 *  discriminata con ``"preview" in result``); ``mailOverrides`` (chiave=reference)
 *  sovrascrive destinatario/oggetto/corpo per ordine prima dell'invio. */
export function sendWebFurnitureOrderBulk(
  orderIds: string[],
  packagesOverrides?: Record<string, WebFurniturePackage[]>,
  opts?: {
    preview?: false;
    mailOverrides?: Record<string, MailOverride>;
    fornitore?: string;
  },
): Promise<WebFurnitureSendBulkResult>;
export function sendWebFurnitureOrderBulk(
  orderIds: string[],
  packagesOverrides: Record<string, WebFurniturePackage[]> | undefined,
  opts: { preview: boolean; mailOverrides?: Record<string, MailOverride>; fornitore?: string },
): Promise<WebFurnitureSendBulkResult | MailPreviewRead>;
export function sendWebFurnitureOrderBulk(
  orderIds: string[],
  packagesOverrides: Record<string, WebFurniturePackage[]> = {},
  opts?: { preview?: boolean; mailOverrides?: Record<string, MailOverride>; fornitore?: string },
): Promise<WebFurnitureSendBulkResult | MailPreviewRead> {
  return apiPost<WebFurnitureSendBulkResult | MailPreviewRead>(
    mailOrderPath(opts?.fornitore ?? "webfurniture", "mail-order-bulk"),
    {
      order_ids: orderIds,
      packages_overrides: packagesOverrides,
      // ``fornitore`` sta nel PATH, non nel body: chiedere a chi mandare e
      // cosa mandare sono due cose, e mischiarle qui cambierebbe il payload
      // dell'invio diretto (retrocompat, vedi webfurnitureApi.test.ts).
      ...(opts?.preview !== undefined || opts?.mailOverrides !== undefined
        ? { preview: opts.preview ?? false, mail_overrides: opts.mailOverrides ?? {} }
        : {}),
    },
  );
}

/** Estrae lo stato mail WF dal raw dell'Order (no fetch). Usato dai badge
 *  in lista per evitare N round-trip: l'OrderRead già espone raw[] con
 *  wf_mail_app_sent_at, wf_mail_imap_confirmed_at, ecc. */
export function deriveWfMailStatusFromRaw(
  raw: Record<string, unknown> | null | undefined,
  fornitore = "webfurniture",
): WebFurnitureMailStatusDetail {
  const profilo = PROFILI_MAIL[fornitore] ?? PROFILO_WF;
  return deriveSupplierMailStatusFromRaw(raw, profilo);
}

/** Chiama il backend per verificare via IMAP se la mail WF è stata recapitata. */
export function verifyWebFurnitureMailSent(
  orderId: string,
  fornitore = "webfurniture",
): Promise<WebFurnitureVerifyMailResult> {
  return apiPost<WebFurnitureVerifyMailResult>(
    mailOrderPath(fornitore, `${encodeURIComponent(orderId)}/verify-mail-sent`),
  );
}
